import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMatchHubData, fetchMatchH2HRaw, fetchMatchLineupsRaw, fetchMatchEventsRaw, fetchMatchInsightsRaw } from '../helpers/matchHubApi';
import {
  mapMatchApiToHubData,
  mapH2HToHub,
  mapLineupsToHub,
  mapEventsToHub,
} from '../helpers/matchHubData.helper';
import { fetchLeagueStandings } from '../../leagueDetail/helpers/leagueDetailApi';
import { normalizeStandingsPayload } from '../../leagueDetail/helpers/mapLeagueStandings.helper';
import type { MatchHubMockData } from '../types/matchHub.types';

export function useMatchHubData(matchId: string): {
  data: MatchHubMockData | null;
  isLoading: boolean;
  isError: boolean;
} {
  const enabled = matchId.length > 0;

  const matchQuery = useQuery({
    queryKey: ['match-hub', matchId],
    queryFn: () => fetchMatchHubData(matchId),
    enabled,
    staleTime: 20 * 1000,
    refetchInterval: 30 * 1000,
  });

  const match = matchQuery.data;
  const leagueId = match?.league?.id;
  const dependentEnabled = enabled && match != null;

  const standingsQuery = useQuery({
    queryKey: ['leagueDetail', 'standings', leagueId],
    queryFn: () => fetchLeagueStandings(leagueId ?? ''),
    enabled: dependentEnabled && typeof leagueId === 'string' && leagueId.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const h2hQuery = useQuery({
    queryKey: ['match-h2h-raw', matchId],
    queryFn: () => fetchMatchH2HRaw(matchId),
    enabled: dependentEnabled,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const lineupsQuery = useQuery({
    queryKey: ['match-lineups-raw', matchId],
    queryFn: () => fetchMatchLineupsRaw(matchId),
    enabled: dependentEnabled,
    staleTime: 60 * 60 * 1000,
  });

  const isLive = match != null && ['LIVE', 'IN_PLAY', 'PAUSED'].includes(match.status);

  const eventsQuery = useQuery({
    queryKey: ['match-events-raw', matchId],
    queryFn: () => fetchMatchEventsRaw(matchId),
    enabled: dependentEnabled,
    staleTime: 30 * 1000,
    refetchInterval: isLive ? 60 * 1000 : false,
  });

  const insightsQuery = useQuery({
    queryKey: ['match-insights-raw', matchId],
    queryFn: () => fetchMatchInsightsRaw(matchId),
    enabled: dependentEnabled,
    staleTime: 6 * 60 * 60 * 1000,
  });

  const data = useMemo<MatchHubMockData | null>(() => {
    if (!match) return null;

    const base = mapMatchApiToHubData(match);
    const homeExtId = match.homeTeam.externalId;
    const awayExtId = match.awayTeam.externalId;

    // --- Standings + form ---
    const normalized = normalizeStandingsPayload(standingsQuery.data ?? null, 'all');
    let standings = base.standings;
    let tablePositions = base.tablePositions;
    let form = base.form;

    if (normalized) {
      const homeTeamId = match.homeTeam.id;
      const awayTeamId = match.awayTeam.id;
      // Show the group that actually contains this match's teams (multi-group
      // competitions like the World Cup expose many groups plus a non-group
      // aggregate); fall back to the first real group when neither is found.
      const matchGroup = normalized.groups.find((group) =>
        group.rows.some((row) => row.teamId === homeTeamId || row.teamId === awayTeamId),
      ) ?? normalized.groups[0];
      const tableRows = matchGroup?.rows ?? [];

      standings = tableRows;

      const homeRow = tableRows.find((r) => r.teamId === homeTeamId);
      const awayRow = tableRows.find((r) => r.teamId === awayTeamId);

      form = {
        home: homeRow?.form ?? [],
        away: awayRow?.form ?? [],
      };

      tablePositions = {
        home: homeRow
          ? { rank: homeRow.rank, played: homeRow.played, goalsFor: homeRow.goalsFor, goalsAgainst: homeRow.goalsAgainst, goalDifference: homeRow.goalDiff, points: homeRow.points }
          : base.tablePositions.home,
        away: awayRow
          ? { rank: awayRow.rank, played: awayRow.played, goalsFor: awayRow.goalsFor, goalsAgainst: awayRow.goalsAgainst, goalDifference: awayRow.goalDiff, points: awayRow.points }
          : base.tablePositions.away,
      };
    }

    // --- Recent form + facts (cross-competition; preferred over the in-tournament
    // standings row, which is empty at the start of a tournament) ---
    const insights = insightsQuery.data;
    let recentRecord: MatchHubMockData['recentRecord'];
    let teamFacts: MatchHubMockData['teamFacts'];
    if (insights) {
      const homeForm = (insights.home.form ?? []).map((f) => f.result);
      const awayForm = (insights.away.form ?? []).map((f) => f.result);
      // Only override when the cross-competition source actually has results.
      form = {
        home: homeForm.length > 0 ? homeForm : form.home,
        away: awayForm.length > 0 ? awayForm : form.away,
      };
      if (insights.home.record && insights.away.record) {
        recentRecord = { home: insights.home.record, away: insights.away.record };
      }
      teamFacts = { home: insights.home.facts ?? null, away: insights.away.facts ?? null };
    }

    // --- H2H ---
    const h2h = mapH2HToHub(h2hQuery.data ?? null, homeExtId);

    // --- Lineups ---
    const { lineups, coaches } = mapLineupsToHub(lineupsQuery.data ?? null, homeExtId, awayExtId);

    // --- Events ---
    const events = mapEventsToHub(eventsQuery.data ?? null, homeExtId);

    return {
      ...base,
      standings,
      tablePositions,
      form,
      h2h,
      lineups,
      coaches,
      events,
      recentRecord,
      teamFacts,
    };
  }, [match, standingsQuery.data, h2hQuery.data, lineupsQuery.data, eventsQuery.data, insightsQuery.data]);

  return {
    data,
    isLoading: matchQuery.isLoading,
    isError: matchQuery.isError,
  };
}
